import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import type { User, AuthResponse } from '@tfa/shared'
import { markFatSecretConnectStarted } from '../lib/fatsecretPolling'

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

/** Окно, выбранное сервером для этого старта (POST /startup-windows/claim). */
export interface StartupWindowClaim {
  id: string
  impressionId: string
  html: string
  vars: Record<string, string>
  isTest: boolean
}

// Resolve a media URL: relative paths (e.g. "/avatars/muscle/xxx.jpg") are
// served by the API host, not the frontend host, so prefix them with API_BASE_URL.
// Absolute URLs (http(s)://) and falsy values pass through unchanged.
export function resolveMediaUrl(url: string | null | undefined): string {
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith('/')) return API_BASE_URL.replace(/\/$/, '') + url
  return url
}

/**
 * Ответ POST /onboarding/refresh-access.
 *  granted       — тариф подтверждён, приложение разблокировано
 *  still_blocked — проверили, доступа в XL пока нет
 *  pending       — синк не уложился в бюджет, доедет фоном (придёт по SSE)
 *  no_lead       — клиент не привязан к лиду XL, сам по себе не вылечится
 *  unavailable   — XL не ответил / не настроен
 */
export interface AccessRefreshResult {
  status: 'granted' | 'still_blocked' | 'pending' | 'no_lead' | 'unavailable'
  product: string | null
  hasXlLead?: boolean
  throttled?: boolean
  checkedAt?: string
  expectedProduct?: string | null
  courseRegistrationUrl?: string
}

// Транзиентные сбои: запрос не дошёл до сервера (WebView усыпили, сеть
// переключилась) или упал таймаут. На проде такой единичный обрыв на холодном
// старте выкидывал клиента на экран «Что-то пошло не так»: в ingress-логах
// упавшего запроса нет вовсе, а beacon об ошибке секундой позже проходит
// с тем же токеном. Ретраим их сами — react-query для ['user','me'] намеренно
// живёт с retry:false, чтобы 401/403 не крутились.
const NETWORK_RETRY_ATTEMPTS = 2
const NETWORK_RETRY_BASE_MS = 400
// Дефолтный таймаут: без него зависший запрос ждёт потолка nginx (60 с) —
// на проде POST /auth/telegram так висел. Загрузка файлов идёт со своим.
const DEFAULT_TIMEOUT_MS = 30_000
const UPLOAD_TIMEOUT_MS = 120_000
// Профиль на холодном старте: с общим таймаутом до экрана ошибки проходило
// 30 с × 3 попытки ≈ полторы минуты лоадера. Сам /auth/me — два индексных
// запроса, ответ за доли секунды; короткий таймаут раньше открывает новое
// соединение, а именно это и лечит повисший после смены сети сокет.
const AUTH_ME_TIMEOUT_MS = 15_000

// Повторить можно только то, что безопасно выполнить дважды. Сетевая ошибка не
// говорит, дошёл ли запрос до сервера, поэтому произвольный POST/PUT/PATCH/DELETE
// не ретраим: повтор задвоил бы запись в дневнике или покупку в магазине.
// Логины и диагностический beacon идемпотентны по смыслу, кардио — по дедупу
// на сервере.
// #626: кардио — сервер узнаёт повтор того же кардио и не пишет его второй раз.
// #635: показатели дня — upsert записи дня теми же значениями; серия и XP
// дедупятся по дню, отчёт куратору уходит только об изменившемся.
const RETRIABLE_NON_GET = new Set(['/auth/telegram', '/auth/login', '/diag/client-error', '/diary/workout/cardio', '/diary/entry'])

// Эндпоинты, которые сами выдают токен. Их 401 — отказ во входе (битый
// initData), а не протухшая сессия: релогин тут ничего не даст и зациклится.
// Остальные /auth/* (прежде всего GET /auth/me) работают по сессии и по 401
// релогинятся как любой другой запрос.
const TOKEN_ISSUING = ['/auth/telegram', '/auth/login']

const matchesPath = (url: string | undefined, paths: Iterable<string>) => {
  // url приходит относительным (baseURL отдельно), но подстрахуемся от полного.
  const path = (url ?? '').split('?')[0]
  return [...paths].some((p) => path === p || path.endsWith(p))
}

/**
 * Решение о повторе запроса — чистая функция, чтобы её можно было проверить
 * тестом отдельно от axios.
 *  - status === undefined → ответа не было: обрыв соединения или таймаут;
 *  - 5xx → сервер прилёг (перезапуск пода, 502/503 от ingress);
 *  - 4xx → повтор бессмыслен, ошибка не пройдёт сама.
 */
export function isRetriableFailure(opts: {
  method?: string
  url?: string
  status?: number
  canceled?: boolean
}): boolean {
  if (opts.canceled) return false
  const transient = opts.status === undefined || (opts.status >= 500 && opts.status <= 599)
  if (!transient) return false
  const method = (opts.method ?? 'get').toLowerCase()
  if (method === 'get' || method === 'head') return true
  return matchesPath(opts.url, RETRIABLE_NON_GET)
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Усыплённый ОС WebView (свернули Telegram, погас экран) не отличает «сервер
// молчит» от «нас заморозили»: XHR дотикивает таймаут уже после пробуждения,
// а в ingress-логах запроса нет вовсе — на проде ровно так beacon с валидным
// токеном приходил сразу за «timeout of 30000ms exceeded» на GET /auth/me.
// Считаем уходы документа в фон: запрос, стартовавший в одной «эпохе» и
// упавший в другой, повторяем после возврата на экран с чистым бюджетом
// ретраев вместо экрана «Нет связи с сервером».
let hiddenEpoch = 0
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') hiddenEpoch += 1
  })
  // pagehide — единственный надёжный сигнал на мобильных (см. useAppForeground).
  window.addEventListener('pagehide', () => { hiddenEpoch += 1 })
}

/** Ждём возврата документа на экран; если он уже виден — сразу. */
function whenVisible(): Promise<void> {
  if (typeof document === 'undefined' || document.visibilityState !== 'hidden') return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      if (document.visibilityState === 'hidden') return
      document.removeEventListener('visibilitychange', done)
      window.removeEventListener('pageshow', done)
      resolve()
    }
    document.addEventListener('visibilitychange', done)
    window.addEventListener('pageshow', done)
  })
}

/**
 * Повторить ли запрос, упавший пока приложение было в фоне. Чистая функция —
 * ради теста. Идемпотентность та же, что у isRetriableFailure: чужой POST
 * после сна дважды не шлём. Один повтор на запрос (`alreadyResumed`): если
 * человек свернул приложение и во время повтора — дальше обычные ретраи.
 */
export function shouldRetryAfterResume(opts: {
  method?: string
  url?: string
  status?: number
  canceled?: boolean
  hiddenEpochAtStart?: number
  hiddenEpochNow: number
  alreadyResumed?: boolean
}): boolean {
  if (opts.alreadyResumed) return false
  if (opts.hiddenEpochAtStart === undefined || opts.hiddenEpochAtStart === opts.hiddenEpochNow) return false
  return isRetriableFailure(opts)
}

/** Служебные пометки на конфиге запроса между попытками интерсептора. */
type RetryConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
  _netRetries?: number
  _hiddenEpoch?: number
  _resumed?: boolean
  /** Кураторский вызов (withCuratorToken): интерсептор ставит ему `tfa_curator_token`. */
  _asCurator?: boolean
  /** Запрос ушёл с кураторским токеном, а не с сессионным. */
  _curatorAuth?: boolean
}

/** Кураторский «вид» общего клиента — см. withCuratorToken. */
type CuratorClient = {
  get(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse>
  delete(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse>
  post(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse>
  put(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse>
  patch(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse>
}

class ApiService {
  private client: AxiosInstance
  private token: string | null = null
  // Re-auth provider registered by the app (re-login via Telegram initData).
  // On a 401 we call it once and retry the original request, so an expired JWT
  // mid-session (e.g. saving a workout set) recovers transparently instead of
  // surfacing «Invalid or expired token».
  private reauthHandler: (() => Promise<boolean>) | null = null
  private reauthInFlight: Promise<boolean> | null = null

  setReauthHandler(fn: (() => Promise<boolean>) | null) {
    this.reauthHandler = fn
  }

  // Single-flight: many parallel requests hitting 401 share one re-auth attempt.
  private runReauth(): Promise<boolean> {
    if (!this.reauthHandler) return Promise.resolve(false)
    if (!this.reauthInFlight) {
      this.reauthInFlight = this.reauthHandler()
        .catch(() => false)
        .finally(() => { this.reauthInFlight = null })
    }
    return this.reauthInFlight
  }

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: DEFAULT_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      // Кураторскому вызову — отложенный кураторский токен; без него (обычный
      // режим куратора, сессия и есть кураторская) — сессионный. Решается на
      // каждой попытке, поэтому и повторы уходят с тем же токеном.
      const curatorToken = (config as RetryConfig)._asCurator ? localStorage.getItem('tfa_curator_token') : null
      ;(config as RetryConfig)._curatorAuth = !!curatorToken
      const token = curatorToken ?? this.token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      // Эпоха на старте именно этой попытки: интерсептор отрабатывает и на повторах.
      ;(config as RetryConfig)._hiddenEpoch = hiddenEpoch
      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const original = error.config as RetryConfig | undefined
        const isLoginCall = !!original && matchesPath(original.url, TOKEN_ISSUING)
        // Token expired mid-session → try to re-authenticate once and replay
        // the failed request. Skip login endpoints (those failing is terminal)
        // and admin-view tokens (no Telegram initData to refresh from).
        // GET /auth/me тоже здесь: на холодном старте с протухшим токеном это
        // первый запрос, и пока его 401 обходил релогин, человек получал
        // экран ошибки и входил только кнопкой «Войти заново».
        // 401 на кураторском токене ничего не говорит о сессии просмотра:
        // релогин выдал бы новый сессионный токен вместо него, а сброс стёр бы
        // сессию клиента. Такой запрос просто падает.
        const curatorAuth = !!original?._curatorAuth
        if (
          error.response?.status === 401 && !curatorAuth &&
          original && !original._retry && !isLoginCall &&
          this.reauthHandler && localStorage.getItem('tfa_admin_view') !== '1'
        ) {
          original._retry = true
          const ok = await this.runReauth()
          if (ok && this.token) {
            original.headers = original.headers || {}
            ;(original.headers as Record<string, string>).Authorization = `Bearer ${this.token}`
            return this.client(original)
          }
        }
        if (error.response?.status === 401 && !curatorAuth) {
          this.token = null
          localStorage.removeItem('tfa_token')
        }

        // Сбой случился, пока приложение было в фоне, — это не сеть, а сон
        // WebView: дожидаемся экрана и повторяем с чистым бюджетом ретраев.
        if (
          original &&
          shouldRetryAfterResume({
            method: original.method,
            url: original.url,
            status: error.response?.status,
            canceled: error.code === 'ERR_CANCELED',
            hiddenEpochAtStart: original._hiddenEpoch,
            hiddenEpochNow: hiddenEpoch,
            alreadyResumed: original._resumed,
          })
        ) {
          original._resumed = true
          original._netRetries = 0
          await whenVisible()
          return this.client(original)
        }

        // Транзиентный сбой (обрыв/таймаут/5xx) — повторяем с backoff 400/800 мс.
        // Идёт после 401-ветки: у сетевой ошибки нет response, они не пересекаются.
        const attempts = original?._netRetries ?? 0
        if (
          original &&
          attempts < NETWORK_RETRY_ATTEMPTS &&
          isRetriableFailure({
            method: original.method,
            url: original.url,
            status: error.response?.status,
            canceled: error.code === 'ERR_CANCELED',
          })
        ) {
          original._netRetries = attempts + 1
          await delay(NETWORK_RETRY_BASE_MS * 2 ** attempts)
          return this.client(original)
        }

        return Promise.reject(error)
      }
    )

    // Admin read-only impersonation: an admin opens the Mini App as a client via
    // a link carrying ?admin_token=<jwt>. Capture it synchronously (before React
    // renders) so the auth flow treats it like a normal returning session, then
    // strip it from the visible URL. We reuse the curator "viewing" keys so the
    // existing banner / onboarding-bypass / read-only handling just work.
    try {
      const params = new URLSearchParams(window.location.search)
      const adminToken = params.get('admin_token')
      if (adminToken) {
        this.token = adminToken
        localStorage.setItem('tfa_token', adminToken)
        localStorage.setItem('tfa_admin_view', '1')
        // Keep the client-side token so we can swap back from curator mode.
        localStorage.setItem('tfa_admin_client_token', adminToken)
        // Optional curator-side token for dual-role users — enables «Режим
        // куратора» as a local token swap (no re-login, stays read-only).
        const curatorToken = params.get('admin_curator_token')
        if (curatorToken) localStorage.setItem('tfa_admin_curator_token', curatorToken)
        else localStorage.removeItem('tfa_admin_curator_token')
        const clientId = params.get('admin_client_id')
        const name = params.get('admin_name')
        // Anchor identity, kept across mode swaps (the curator screen needs
        // viewingClientId cleared, so we restore from these dedicated keys).
        if (clientId) localStorage.setItem('tfa_admin_client_id', clientId)
        if (name) localStorage.setItem('tfa_admin_client_name', name)
        // Initial view is the client side → drive the read-only banner.
        if (clientId) localStorage.setItem('tfa_viewing_client_id', clientId)
        if (name) localStorage.setItem('tfa_viewing_client_name', name)
        params.delete('admin_token')
        params.delete('admin_curator_token')
        params.delete('admin_client_id')
        params.delete('admin_name')
        const qs = params.toString()
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash)
        return
      }
    } catch {
      // URL parsing unavailable — fall through to the persisted token below.
    }

    // Load token from localStorage
    const savedToken = localStorage.getItem('tfa_token')
    if (savedToken) {
      this.token = savedToken
    }
  }

  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('tfa_token', token)
    } else {
      localStorage.removeItem('tfa_token')
    }
  }

  getToken() {
    return this.token
  }

  /**
   * Диагностический beacon (bug #213): шлёт детали ошибки инициализации на бэкенд
   * (логи API + админ-бот), чтобы ловить причину на мобиле, где консоль недоступна.
   * Best-effort — никогда не бросает (диагностика не должна ломать UI).
   */
  async reportClientError(payload: {
    context?: string
    status?: number
    code?: string
    endpoint?: string
    method?: string
    message?: string
    platform?: string
    isCurator?: boolean
    hasToken?: boolean
    flags?: Record<string, string>
  }): Promise<void> {
    try {
      await this.client.post('/diag/client-error', payload)
    } catch {
      /* диагностика — тихо глотаем */
    }
  }

  // Auth
  async loginWithTelegram(initData: string, forceMode?: 'client' | 'curator'): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/auth/telegram', { initData, forceMode })
    this.setToken(data.token)
    return data
  }

  async loginWithEmail(email: string): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>('/auth/login', { email })
    this.setToken(data.token)
    return data
  }

  async getMe(): Promise<User> {
    const { data } = await this.client.get<User>('/auth/me', { timeout: AUTH_ME_TIMEOUT_MS })
    return data
  }

  /** Сохранить таймзону устройства (вариант A: «день» считается по ней). */
  async setTimezone(timezone: string): Promise<void> {
    await this.client.put('/users/timezone', { timezone })
  }

  /** Сообщить бэкенду сборку Mini App (зовётся только при её смене). */
  async setAppBuild(buildId: string): Promise<void> {
    await this.client.put('/users/app-build', { buildId })
  }

  /** #347: загрузить своё фото профиля (jpg/png/webp, до 5 МБ). */
  async uploadMyAvatar(file: File): Promise<{ avatarUrl: string }> {
    const form = new FormData()
    form.append('file', file)
    const { data } = await this.client.post('/users/me/avatar', form, {
      // 5 МБ с мобильного интернета не укладываются в общий 30-секундный таймаут.
      timeout: UPLOAD_TIMEOUT_MS,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  }

  /** #347: убрать своё фото — вернётся аватар из Telegram. */
  async deleteMyAvatar(): Promise<void> {
    await this.client.delete('/users/me/avatar')
  }

  /** #602: после возврата с оплаты продления — свежий срок доступа из Акселя. */
  async refreshXlAccess(): Promise<{ refreshed: boolean; subscriptionEnd: string | null; completedAt: string | null }> {
    const { data } = await this.client.post('/users/me/xl-refresh')
    return data
  }


  async getDashboard() {
    const { data } = await this.client.get('/users/dashboard')
    return data
  }

  // Даты сдачи видео техники (#234). earned = принёс ли день XP (в пределах
  // недельного лимита = дни программы); сверх — техника засчитана без XP.
  async getTechniqueDates(params?: { startDate?: string; endDate?: string }): Promise<{ dates: Array<{ date: string; earned: boolean }> }> {
    const { data } = await this.client.get('/diary/technique-dates', { params })
    return data
  }

  /**
   * Дни, за которые отчёт реально принёс XP (#507). Шкала «Отчёты» считает
   * заработанное по этим дням, а не по факту записи в дневнике: правки задним
   * числом и бэкфилл FatSecret пишут дневник без начисления.
   */
  async getXpDays(params?: { startDate?: string; endDate?: string }): Promise<{ days: Array<{ date: string; types: string[] }> }> {
    const { data } = await this.client.get('/diary/xp-days', { params })
    return data
  }

  async getStreakStatus() {
    const { data } = await this.client.get('/actions/streak/status')
    return data
  }

  /**
   * Исторические «дни в режиме» (даты YYYY-MM-DD) — для календаря серий.
   * `days` — дни с активностью, `frozenDays` — пропуски, спасённые заморозкой
   * (мостят серию, рисуются отдельным значком). `pausedDays` (#532) — дни паузы
   * программы: тоже мостят серию, в календаре под той же снежинкой. `graceDays`
   * (#586) — пропуск, за который таймер не наказал: серию не рвёт, значка нет.
   */
  async getActiveDays(): Promise<{ days: string[]; frozenDays?: string[]; lifeLostDays?: string[]; pausedDays?: string[]; graceDays?: string[]; claimedMilestones?: number[]; streakRecord?: number }> {
    const { data } = await this.client.get('/actions/streak/active-days')
    return data
  }

  async getDailyStatus(): Promise<{
    nutrition: boolean; training: boolean; sleep: boolean; steps: boolean; weight: boolean;
    videoReport: boolean;
    weeklyPhotoProgress: boolean; weeklyMeasurements: boolean;
    completedCount: number; totalCount: number;
  }> {
    const { data } = await this.client.get('/actions/daily-status')
    return data
  }

  // Shop
  async getShopBalance() {
    const { data } = await this.client.get('/shop/balance')
    return data
  }

  async getShopItems() {
    const { data } = await this.client.get('/shop/items')
    return Array.isArray(data) ? data : (data?.items ?? [])
  }

  async buyItem(itemId: string) {
    const { data } = await this.client.post('/shop/buy', { itemId })
    return data
  }

  async getShopPurchases() {
    const { data } = await this.client.get('/shop/purchases')
    return data as {
      purchases: Array<{ id: string; itemId: string; type: string; name: string; category: string; price: number; createdAt: string }>
    }
  }

  // Admin-managed physical products (за TuCoin, с доставкой)
  async getShopProducts() {
    const { data } = await this.client.get('/shop/products')
    return data as {
      items: Array<{
        id: string
        name: string
        description: string
        imageUrl: string | null
        priceTucoin: number
        deliveryNote: string | null
        availableTo: string | null
        affordable: boolean
      }>
      balance: number
      // #255: график отправки призов — ближайшая дата + периодичность (мес).
      prizeShipment?: { nextDate: string | null; intervalMonths: number }
    }
  }

  async createShopOrder(
    productId: string,
    delivery: {
      city: string
      ozonPickupAddress: string
      recipientAddress: string
      recipientName: string
      recipientPhone: string
      userNote?: string
    },
  ) {
    const { data } = await this.client.post(`/shop/products/${productId}/order`, delivery)
    return data as { ok: boolean; orderId: string; balance: number }
  }

  // Корзина (#256): несколько товаров одним заказом с единой доставкой.
  async createShopBulkOrder(
    items: Array<{ productId: string; quantity: number }>,
    delivery: {
      city: string
      ozonPickupAddress: string
      recipientAddress: string
      recipientName: string
      recipientPhone: string
      userNote?: string
    },
  ) {
    const { data } = await this.client.post('/shop/orders/bulk', { items, delivery })
    return data as { ok: boolean; orderGroupId: string; count: number; total: number; balance: number }
  }

  async getShopOrders() {
    const { data } = await this.client.get('/shop/orders')
    return data as {
      items: Array<{ id: string; productName: string; priceTucoin: number; status: string; createdAt: string }>
    }
  }

  // Leaderboard
  async getLeaderboard(params?: { page?: number; limit?: number }) {
    const { data } = await this.client.get('/leaderboard', { params })
    const users = data.users || data
    return users.map((u: Record<string, unknown>, index: number) => ({
      ...u,
      userId: u.userId ?? u.id,
      rank: u.rank ?? index + 1,
      avatarUrl: u.avatarUrl ?? u.telegramAvatar ?? null,
      firstName: (u.firstName as string) || 'Anonymous',
      lastName: (u.lastName as string) || '',
    }))
  }

  async getMyRank() {
    const { data } = await this.client.get('/leaderboard/me')
    return data
  }

  async getLeagueRoom() {
    const { data } = await this.client.get('/leaderboard/room')
    return data
  }

  /** Лидерборд v2 — комната клиента с микс USER+BOT, ранжирована по weeklyXp. */
  async getLeaderboardRoomV2(): Promise<{
    room: { id: string; league: string; weekStart: string; weekEnd: string } | null
    config: { displayName: string; iconEmoji: string | null; roomSize: number; promoteCount: number; demoteCount: number } | null
    members: Array<{
      id: string
      rank: number
      weeklyXp: number
      displayName: string
      avatarUrl: string | null
      streakDays: number
      program: string
      isMe: boolean
    }>
    myRank: number | null
    /** #622: щит от понижения — Клуб и ТФА в Золоте/Элите, раз в месяц. */
    metaShield?: { eligible: boolean; activeThisWeek: boolean; canActivate: boolean; availableAt: string | null } | null
  }> {
    const { data } = await this.client.get('/leaderboard/room/v2')
    return data
  }

  /** #622: включить щит от понижения на текущую неделю. */
  async activateMetaShield(): Promise<{ success: boolean; usedAt: string }> {
    const { data } = await this.client.post('/leaderboard/meta-shield')
    return data
  }

  // Challenges
  async getChallenges() {
    const { data } = await this.client.get('/challenges')
    return data
  }

  /** Челленджи v2 (общий чат + #тэг) — список со статусами и счётчиками. */
  async getChallengesV2(): Promise<{
    workingChatId: string | null
    workingChatName: string | null
    workingChatLink: string | null
    items: Array<{
      id: string
      name: string
      description: string
      photoUrl: string | null
      topicLink: string | null
      tag: string
      xpPerCompletion: number
      maxTucoin: number
      dailyLimit: number
      durationDays: number
      startDate: string | null
      endDate: string | null
      status: 'PLANNED' | 'ANNOUNCED' | 'ACTIVE' | 'FINISHED'
      publicSuccessTemplate: string | null
      // personalRuleText удалён
      todayCount: number
      totalCount: number
      daysCompleted: number
      canCompleteToday: boolean
    }>
  }> {
    const { data } = await this.client.get('/challenges/v2')
    return data
  }

  async joinChallenge(challengeId: string) {
    const { data } = await this.client.post(`/challenges/${challengeId}/join`)
    return data
  }

  async getSurvivalGames() {
    const { data } = await this.client.get('/challenges/survival-games')
    return data
  }

  async joinSurvivalGame(gameId: string) {
    const { data } = await this.client.post(`/challenges/survival-games/${gameId}/join`)
    return data
  }

  // Lectures
  async getModules() {
    // Prefer XL-backed data; fall back to legacy modules if client has no XL
    // lead attached. Same shape either way.
    try {
      const { data } = await this.client.get('/lectures/xl-modules')
      if (data.hasXlData) return this.normalizeModules(data.modules)
    } catch (err) {
      // ignore — fall through to legacy
    }
    const { data } = await this.client.get('/lectures/modules')
    return this.normalizeModules(data.modules || data)
  }

  private normalizeModules(modules: Array<Record<string, unknown>>): any[] {
    return (modules || []).map((m: any) => ({
      ...m,
      completedCount: m.completedCount ?? m.completedLectures ?? 0,
      totalCount: m.totalCount ?? m.totalLectures ?? 0,
      isLocked: m.isLocked ?? m.state === 'locked',
      // Три тарифа (#544): курс ТФА серым — состав виден, уроки не открываются.
      gray: m.gray === true,
      lectures: ((m.lectures as Array<Record<string, unknown>>) || []).map((l: any) => ({
        ...l,
        isCompleted: l.isCompleted ?? l.completed ?? false,
        isLocked: l.isLocked ?? l.state === 'locked',
      })),
    }))
  }

  // Re-sync the current client's courses from Prodamus XL on demand.
  // Returns whether the client is linked to an XL lead and whether they now
  // have any courses. Safe to call for unlinked clients (returns hasXlLead:false).
  async refreshCourses() {
    const { data } = await this.client.post('/lectures/xl-modules/refresh')
    return data as { hasXlLead: boolean; hasCourses: boolean }
  }

  /**
   * Перепроверка доступа для клиента без подтверждённого тарифа (блокирующий
   * экран). Отличается от refreshCourses: работает без продукта, возвращает
   * сам продукт и не пинает медленную выкачку прогрессов зря.
   *
   * Фолбэк на старый эндпоинт при 404 — бандл Mini App и API деплоятся
   * независимо, клиент может открыть новый экран на API без этого маршрута.
   */
  async refreshAccess(): Promise<AccessRefreshResult> {
    try {
      const { data } = await this.client.post('/onboarding/refresh-access')
      return data as AccessRefreshResult
    } catch (err) {
      if ((err as { response?: { status?: number } })?.response?.status !== 404) throw err
      const legacy = await this.refreshCourses()
      return {
        status: legacy.hasXlLead ? (legacy.hasCourses ? 'granted' : 'still_blocked') : 'no_lead',
        product: null,
        hasXlLead: legacy.hasXlLead,
      }
    }
  }

  async getXlStepTheory(stepId: string) {
    const { data } = await this.client.get(`/lectures/xl-modules/${stepId}/theory`)
    return data as {
      stepId: string
      title: string
      theoryHtml: string | null
      courseId: string
      courseName: string
      status: string
      isCurrent: boolean
      completed: boolean
    }
  }

  /**
   * #413: временная ссылка на вложение урока (презентация, гайд). В теории XL
   * такие ссылки приходят без href — только с id файла, а адрес выдаёт XL и
   * ненадолго, поэтому запрашиваем его в момент тапа.
   */
  async getXlStepAttachmentUrl(stepId: string, fileId: string) {
    const { data } = await this.client.get(
      `/lectures/xl-modules/${encodeURIComponent(stepId)}/attachment/${encodeURIComponent(fileId)}`,
    )
    return (data as { url: string }).url
  }

  // Завершение XL-шага (урока) — отдельный endpoint (XL-уроки не в легаси Lecture).
  async completeXlLecture(stepId: string) {
    const { data } = await this.client.post(`/lectures/xl-modules/${stepId}/complete`)
    return data
  }

  async getLecture(lectureId: string) {
    const { data } = await this.client.get(`/lectures/${lectureId}`)
    return data
  }

  // #205: даты просмотра лекций — для отметки «Лекция» в шкале «Отчёты».
  async getLectureCompletions(): Promise<{ completedAt: string[] }> {
    const { data } = await this.client.get('/lectures/completions')
    return data
  }

  async completeLecture(lectureId: string) {
    const { data } = await this.client.post(`/lectures/${lectureId}/complete`)
    return data
  }

  // Diary
  async getDiary(date: string): Promise<{
    entry: any | null
    nutritionGoal: {
      dayType: 'REGULAR' | 'TRAINING'
      calories: number
      protein: number
      fat: number
      carbs: number
      fiber: number | null
      curatorName: string | null
      updatedAt: string
    } | null
  }> {
    const startDate = date
    const endDate = date
    const { data } = await this.client.get('/diary', { params: { startDate, endDate } })
    const entries = (data.entries || []) as any[]
    // Защита от легаси-данных: если за день оказалось несколько строк DiaryEntry
    // (исторически могли возникать дубли со смещённой датой), показатели могли
    // лежать на разных строках. Берём первую как основу и дозаполняем пустые
    // скалярные поля из соседних строк того же дня — чтобы ничего не «терялось».
    let entry = entries[0] || null
    if (entry && entries.length > 1) {
      entry = { ...entry }
      const scalarKeys = ['calories', 'protein', 'fat', 'carbs', 'fiber', 'sleep', 'sleepQuality', 'steps', 'weight']
      for (const k of scalarKeys) {
        if (entry[k] == null) {
          const alt = entries.find((e) => e[k] != null)
          if (alt) entry[k] = alt[k]
        }
      }
      // metrics — объект доп. метрик: сливаем непустые поля со всех строк дня.
      const mergedMetrics: Record<string, number> = {}
      for (const e of entries) {
        if (e.metrics && typeof e.metrics === 'object') Object.assign(mergedMetrics, e.metrics)
      }
      if (Object.keys(mergedMetrics).length) entry.metrics = { ...mergedMetrics, ...(entry.metrics ?? {}) }
    }
    return {
      entry,
      nutritionGoal: data.nutritionGoal ?? null,
    }
  }

  /**
   * #537: своя норма КБЖУ. `selfManaged` — можно ли её редактировать: на
   * ступенях норму согласует куратор, в Клубе человек задаёт её сам.
   */
  async getMyNutritionGoals(): Promise<{
    selfManaged: boolean
    goals: Array<{ dayType: 'REGULAR' | 'TRAINING'; calories: number; protein: number; fat: number; carbs: number; fiber: number | null }>
  }> {
    const { data } = await this.client.get('/diary/nutrition-goals')
    return { selfManaged: !!data?.selfManaged, goals: data?.goals ?? [] }
  }

  async saveMyNutritionGoals(goals: Array<{ dayType: 'REGULAR' | 'TRAINING'; calories: number; protein: number; fat: number; carbs: number; fiber?: number }>) {
    const { data } = await this.client.put('/diary/nutrition-goals', { goals })
    return data
  }

  async getDiaryEntries(params?: { date?: string; startDate?: string; endDate?: string }) {
    const { data } = await this.client.get('/diary', { params })
    return data
  }

  async addMeal(meal: {
    diaryEntryId: string
    mealType: 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'SNACK'
    description?: string
    photoUrl?: string
    calories?: number
    protein?: number
    fat?: number
    carbs?: number
  }) {
    const { data } = await this.client.post('/diary/meals', meal)
    return data
  }

  async updateMeal(
    id: string,
    patch: {
      description?: string | null
      photoUrl?: string | null
      calories?: number | null
      protein?: number | null
      fat?: number | null
      carbs?: number | null
    },
  ) {
    const { data } = await this.client.patch(`/diary/meals/${id}`, patch)
    return data
  }

  async createDiaryEntry(entry: {
    date: string
    meals?: Record<string, unknown>
    water?: number
    sleep?: number
    steps?: number
    weight?: number
    notes?: string
    calories?: number
    protein?: number
    fat?: number
    carbs?: number
    fiber?: number
    /** Доп. метрики питания (вода, натрий, …) — частичный апдейт; null чистит. */
    metrics?: Record<string, number | null>
  }) {
    const { data } = await this.client.post('/diary/entry', entry)
    return data
  }

  /** Включённые клиентом метрики питания (массив ключей из NUTRITION_METRICS). */
  async getNutritionMetrics(): Promise<{ enabled: string[] }> {
    const { data } = await this.client.get('/diary/nutrition-metrics')
    return data
  }

  /** Клиент включает/убирает метрики питания. */
  async updateNutritionMetrics(enabled: string[]): Promise<{ enabled: string[] }> {
    const { data } = await this.client.put('/diary/nutrition-metrics', { enabled })
    return data
  }

  /** #400: цель по шагам — ставит куратор, клиент только читает. */
  async getStepsGoal(): Promise<{ stepsGoal: number | null; defaultGoal: number }> {
    const { data } = await this.client.get('/diary/steps-goal')
    return data
  }

  async getMeasurements() {
    const { data } = await this.client.get('/diary/measurements')
    return data
  }

  async addMeasurement(measurement: {
    date: string
    weight?: number
    arms?: number
    chest?: number
    waist?: number
    belly?: number
    pelvis?: number
    thigh?: number
    calf?: number
    neck?: number
    hips?: number
  }) {
    const { data } = await this.client.post('/diary/measurements', measurement)
    return data
  }

  async getProgressPhotos() {
    const { data } = await this.client.get('/diary/photos')
    return data
  }

  // ── Допы: анкеты обратной связи / NPS ──
  async getSurveyStatus(): Promise<{
    stageWeek: number
    product: string | null
    extras: Array<{
      slot: 'PHOTO_START' | 'PHOTO_MONTH1' | 'PHOTO_MONTH2' | 'PHOTO_FINAL' | 'FEEDBACK' | 'NPS'
      kind: 'PHOTO' | 'FEEDBACK' | 'NPS'
      tucoin: number
      label: string
      openWeek: number
      open: boolean
      claimed: boolean
    }>
  }> {
    const { data } = await this.client.get('/surveys/status')
    return data
  }

  async submitFeedback(responses: Record<string, unknown>) {
    const { data } = await this.client.post('/surveys/feedback', { responses })
    return data
  }

  async submitNps(responses: Record<string, unknown>) {
    const { data } = await this.client.post('/surveys/nps', { responses })
    return data
  }

  async submitNextStageInterest() {
    const { data } = await this.client.post('/surveys/next-stage-interest', {})
    return data
  }

  async getUnreadCommentsCount(): Promise<{
    total: number
    byType: {
      DIARY_ENTRY: number
      DIARY_MEAL: number
      MEASUREMENT: number
      PROGRESS_PHOTO: number
      WORKOUT_EXERCISE: number
      WORKOUT_ENTRY: number
    }
  }> {
    const { data } = await this.client.get('/diary/unread-comments-count')
    return data
  }

  async getUnreadCommentsByDate(startDate?: string, endDate?: string): Promise<{ byDate: Record<string, number> }> {
    const { data } = await this.client.get('/diary/unread-comments-by-date', {
      params: { startDate, endDate },
    })
    return data
  }

  /** Явно пометить прочитанными комментарии за день (когда клиент открыл шторку). */
  async markDiaryCommentsRead(date?: string, targetTypes?: string[]): Promise<{ updated: number }> {
    const { data } = await this.client.post('/diary/comments/mark-read', { date, targetTypes })
    return data
  }

  /** #230/#237: клиент оставляет/отвечает комментарий (двусторонний тред). */
  async postClientComment(body: { targetType: string; targetId: string; text: string }) {
    const { data } = await this.client.post('/diary/comment', body)
    return data
  }

  /** #266/#270: единый инбокс — все комментарии куратора одним списком. */
  async getAllComments(): Promise<{ comments: import('../components/modals/CommentsInboxSheet').InboxComment[] }> {
    const { data } = await this.client.get('/diary/all-comments')
    return data
  }

  /** #266: пометить прочитанными ВСЕ комментарии куратора (открытие инбокса). */
  async markAllCommentsRead(): Promise<{ updated: number }> {
    const { data } = await this.client.post('/diary/all-comments/mark-read', {})
    return data
  }

  // Workouts (doc 22)
  async getWorkoutProgram() {
    const { data } = await this.client.get('/diary/workout/program')
    return data
  }

  // Клуб: библиотека шаблонов программ + самостоятельная активация.
  async getWorkoutTemplates(): Promise<{
    templates: Array<{
      id: string
      name: string
      daysCount: number
      days: Array<{ dayNumber: number; exercises: Array<{ id: string; exercise?: { name: string } | null; customName?: string | null }> }>
    }>
    /** #570: список ограничен купленными программами (тариф «Программа тренировок»). */
    restricted?: boolean
    /** #570: покупка не найдена — программ нет, клиента ведём в заботу. */
    purchaseMissing?: boolean
    /** #570: тарифы продуктов ещё не подтянулись из Акселя — стоит заглянуть позже. */
    purchasePending?: boolean
  }> {
    const { data } = await this.client.get('/diary/workout/templates')
    return data
  }

  /**
   * #539: правка своей программы — тарифы без куратора меняют её сами, и
   * правки живут в активной программе, а не на одну тренировку.
   */
  async addOwnProgramExercise(dayNumber: number, body: { exerciseId: string; sets?: number | null; reps?: string | null; duration?: number | null }) {
    const { data } = await this.client.post(`/diary/workout/program/days/${dayNumber}/exercises`, body)
    return data
  }

  /** #607: подходы и диапазон повторений своего упражнения. */
  async updateOwnProgramExercise(exerciseId: string, body: { sets?: number | null; reps?: string | null }) {
    const { data } = await this.client.patch(`/diary/workout/program/exercises/${exerciseId}`, body)
    return data
  }

  async removeOwnProgramExercise(exerciseId: string) {
    const { data } = await this.client.delete(`/diary/workout/program/exercises/${exerciseId}`)
    return data
  }

  async reorderOwnProgramExercises(dayNumber: number, exerciseIds: string[]) {
    const { data } = await this.client.put(`/diary/workout/program/days/${dayNumber}/order`, { exerciseIds })
    return data
  }

  async activateProgramFromTemplate(templateId: string) {
    const { data } = await this.client.post(`/diary/workout/from-template/${templateId}`, {})
    return data
  }

  async getWorkoutEntries(startDate?: string, endDate?: string) {
    const { data } = await this.client.get('/diary/workout/entries', { params: { startDate, endDate } })
    return data
  }

  /**
   * Лёгкая выдача тренировок: даты, длительность и счётчики упражнений/подходов —
   * без упражнений, мышц и комментариев (полная весила ~450 КБ). Комментарии
   * прочитанными не помечает.
   */
  async getWorkoutEntrySummaries(): Promise<{ entries: Array<{
    id: string
    date: string
    programDayId: string | null
    durationMinutes: number | null
    exercises: Array<{ id: string; sets: Array<{ id: string }> }>
    cardioActivities: Array<{ id: string }>
  }> }> {
    const { data } = await this.client.get('/diary/workout/entries', { params: { view: 'summary' } })
    return data
  }

  async getWorkoutEntry(entryId: string) {
    const { data } = await this.client.get(`/diary/workout/entries/${entryId}`)
    return data
  }

  /** Ёмкая сводка прошлой тренировки: упражнения, подходы, время, % прироста по 1ПМ. */
  async getWorkoutEntrySummary(entryId: string): Promise<{
    summary: {
      id: string
      date: string
      dayNumber: number | null
      durationMinutes: number | null
      exerciseCount: number
      totalSets: number
      exercises: Array<{
        id: string
        name: string
        sets: Array<{ setNumber: number; weight: number | null; reps: number | null; rir: number | null }>
        setsCount: number
        est1RM: number | null
        prevEst1RM: number | null
        gainPct: number | null
        /** #644: в чём считан gainPct — расч. 1ПМ или повторы (упражнение без веса). */
        progressMetric?: 'e1rm' | 'reps' | null
        // #306: тред комментариев упражнения (клиент может начать сам)
        comments: import('../components/CuratorCommentThread').CuratorCommentDto[]
      }>
      cardio: Array<{ name: string; durationMinutes: number | null; avgHeartRate: number | null }>
      // #306: комментарии к тренировке в целом
      comments: import('../components/CuratorCommentThread').CuratorCommentDto[]
    }
  }> {
    const { data } = await this.client.get(`/diary/workout/entries/${entryId}/summary`)
    return data
  }

  async saveWorkoutEntry(entry: any) {
    const { data } = await this.client.post('/diary/workout/entries', entry)
    return data
  }

  // Клиент начал тренировку — уведомить куратора (без записи в дневник).
  async startWorkout(dayNumber?: number) {
    const { data } = await this.client.post('/diary/workout/start', dayNumber != null ? { dayNumber } : {})
    return data
  }

  // Серверная сессия активной тренировки (для алертов 3ч/12ч от scheduler).
  async startWorkoutSession(body: { programDayId?: string | null; programDayNumber?: number | null; date: string }) {
    const { data } = await this.client.post('/diary/workout/session', body)
    return data as { session: { id: string } }
  }

  async syncWorkoutSession(sessionId: string, snapshot: any) {
    const { data } = await this.client.put(`/diary/workout/session/${sessionId}`, snapshot)
    return data
  }

  /**
   * #420: незакрытая тренировка с серверным снимком прогресса — запасной путь
   * восстановления, когда память телефона не пережила выгрузку мини-приложения.
   */
  async getActiveWorkoutSession() {
    const { data } = await this.client.get('/diary/workout/session/active')
    return data as {
      session: {
        id: string
        workoutDate: string
        startedAt: string
        programDayId: string | null
        programDayNumber: number | null
        snapshot: {
          durationMinutes?: number | null
          exercises?: Array<{
            exerciseId?: string | null
            customName?: string | null
            orderNum?: number
            sets?: Array<{ setNumber: number; weight?: number | null; reps?: number | null; rir?: number | null }>
          }>
        } | null
      } | null
    }
  }

  async cancelWorkoutSession(sessionId: string) {
    const { data } = await this.client.delete(`/diary/workout/session/${sessionId}`)
    return data
  }

  // Отдельная кардио-активность: ≥20 мин → +5 XP, максимум 2 в неделю.
  // #286: форма с опциональными км / средним пульсом / калориями.
  async saveCardioActivity(body: { name?: string; durationMinutes: number; date?: string; distanceKm?: number; avgHeartRate?: number; caloriesBurned?: number }) {
    const { data } = await this.client.post('/diary/workout/cardio', body)
    // #626: xpReason — почему XP ноль; duplicate — повтор уже сохранённого кардио.
    return data as { entry: any; xpAwarded: number; xpReason?: string | null; duplicate?: boolean; weeklyCardioCount?: number }
  }

  // Клиент оставляет комментарий по упражнению → пуш куратору в топик.
  async sendExerciseComment(exerciseName: string, text: string) {
    const { data } = await this.client.post('/diary/workout/exercise-comment', { exerciseName, text })
    return data
  }

  // Комментарий клиента в переписку по упражнению сохранённой тренировки
  // (персистится в clientNotes + уведомляет куратора).
  async addExerciseClientNote(exerciseEntryId: string, text: string): Promise<{ clientNotes: Array<{ text: string; at: string | null }> }> {
    const { data } = await this.client.post(`/diary/workout/exercise/${exerciseEntryId}/client-note`, { text })
    return data
  }

  async updateWorkoutEntry(entryId: string, entry: any) {
    const { data } = await this.client.put(`/diary/workout/entries/${entryId}`, entry)
    return data
  }

  async deleteWorkoutEntry(entryId: string) {
    const { data } = await this.client.delete(`/diary/workout/entries/${entryId}`)
    return data
  }

  /** История упражнения: библиотечное — по exerciseId, кастомное — по name (customName). */
  async getExerciseHistory(key: string | { exerciseId?: string | null; name?: string | null }) {
    const params: Record<string, string> = {}
    if (typeof key === 'string') params.exerciseId = key
    else if (key.exerciseId) params.exerciseId = key.exerciseId
    else if (key.name) params.name = key.name
    const { data } = await this.client.get('/diary/workout/exercise-history', { params })
    return data
  }

  /** Последние подходы (вес/повторы/RIR) по каждому упражнению — для префилла. */
  async getWorkoutLastSets(): Promise<{
    lastByExercise: Record<string, Array<{ setNumber: number; weight: number | null; reps: number | null; rir: number | null }>>
    lastByCustom?: Record<string, Array<{ setNumber: number; weight: number | null; reps: number | null; rir: number | null }>>
  }> {
    const { data } = await this.client.get('/diary/workout/last-sets')
    return data
  }

  async getExerciseLatestComment(key: string | { exerciseId?: string | null; name?: string | null }) {
    const params: Record<string, string> = {}
    if (typeof key === 'string') params.exerciseId = key
    else if (key.exerciseId) params.exerciseId = key.exerciseId
    else if (key.name) params.name = key.name
    const { data } = await this.client.get('/diary/workout/exercise-latest-comment', { params })
    return data as {
      comment: {
        id: string
        text: string
        createdAt: string
        sessionDate: string | null
        curator: { id: string; name: string } | null
      } | null
    }
  }

  async getDiaryExercises(params?: { kind?: 'STRENGTH' | 'CARDIO'; search?: string }) {
    const { data } = await this.client.get('/diary/workout/exercises', { params })
    return data as {
      exercises: Array<{
        id: string
        name: string
        description?: string | null
        photos: string[]
        defaultDuration?: number | null
        defaultIntensity?: string | null
        defaultHrZone?: string | null
        embedCode?: string | null
        category: { id: string; name: string; workoutType: { id: string; name: string; kind: 'STRENGTH' | 'CARDIO' } }
        primaryMuscles?: Array<{ id: string; name: string; imageUrl?: string | null }>
        secondaryMuscles?: Array<{ id: string; name: string; imageUrl?: string | null }>
      }>
    }
  }

  // AI Chat — one continuous thread per client, shared with the Telegram bot.
  async sendMessage(message: string): Promise<{
    userMessage: { id: string; role: 'user'; content: string; createdAt: string }
    assistantMessage: { id: string; role: 'assistant'; content: string; createdAt: string }
  }> {
    const { data } = await this.client.post('/ai/chat', { message })
    return data
  }

  async getChatHistory(limit = 50): Promise<{
    messages: Array<{ id: string; role: 'user' | 'assistant'; content: string; createdAt: string }>
  }> {
    const { data } = await this.client.get('/ai/history', { params: { limit } })
    return data
  }

  // Goals
  async getDailyGoals() {
    const { data } = await this.client.get('/goals/daily')
    return data
  }

  async getWeeklyGoals() {
    const { data } = await this.client.get('/goals/weekly')
    return data
  }

  // Notifications
  async getNotifications(params: { limit?: number; offset?: number; unreadOnly?: boolean } = {}): Promise<{
    notifications: Array<{
      id: string
      type: string
      title: string
      body: string
      isRead: boolean
      sentAt: string
    }>
    unreadCount: number
  }> {
    const { data } = await this.client.get('/notifications', { params })
    // Defensive: legacy DB rows persisted before the server-side interpolation
    // fix may start with an orphan ", " / "! " / ": " when a {firstName}-style
    // placeholder substituted to an empty value. Strip it on read so existing
    // notifications render cleanly until they age out.
    if (data?.notifications && Array.isArray(data.notifications)) {
      for (const n of data.notifications) {
        if (typeof n?.body === 'string') {
          n.body = n.body.replace(/^[\s]*[,!:;]\s+/, '')
        }
      }
    }
    return data
  }

  async markNotificationsRead(notificationIds: string[]) {
    const { data } = await this.client.post('/notifications/mark-read', { notificationIds })
    return data
  }

  async markAllNotificationsRead() {
    const { data } = await this.client.post('/notifications/mark-all-read')
    return data
  }

  // Onboarding
  async completeOnboarding(data: {
    gender: string
    goal?: string
    level1?: Record<string, unknown>
    level2?: Record<string, unknown>
    requestedStartDate?: string
  }) {
    const response = await this.client.post('/onboarding/complete', data)
    return response.data
  }

  async getStreamCohorts() {
    const response = await this.client.get('/onboarding/stream-cohorts')
    return response.data
  }

  /**
   * #579: «Я вступил(а)» на обязательном экране вступления в канал и чат.
   * Тарифам без анкеты (Программа, Тренинг) этот шаг заменяет соответствующий
   * шаг анкеты, поэтому отметка серверная — переустановка Telegram её не стирает.
   */
  async markChatsJoined(): Promise<{ chatsJoinedAt: string | null }> {
    const { data } = await this.client.post('/onboarding/chats-joined')
    return data
  }

  async markStoriesSeen(data: { testWeek?: boolean; mainMode?: boolean }) {
    const response = await this.client.post('/onboarding/stories-seen', data)
    return response.data
  }

  // ── Стартовые окна (админ-настраиваемые HTML-окна при старте) ──
  async claimStartupWindow(trigger: 'cold' | 'foreground'): Promise<{ window: StartupWindowClaim | null }> {
    const response = await this.client.post('/startup-windows/claim', { trigger })
    return response.data
  }

  async dismissStartupWindow(impressionId: string) {
    const response = await this.client.post(`/startup-windows/impressions/${impressionId}/dismiss`)
    return response.data
  }

  async clickStartupWindow(impressionId: string, data: { action: 'link' | 'screen'; target: string }) {
    const response = await this.client.post(`/startup-windows/impressions/${impressionId}/click`, data)
    return response.data
  }

  async resetOnboarding() {
    const response = await this.client.post('/onboarding/reset')
    return response.data
  }

  async resetWorkouts(): Promise<{ deleted: number }> {
    const { data } = await this.client.post('/diary/reset/workouts')
    return data
  }

  async resetMeasurements(): Promise<{ deleted: number }> {
    const { data } = await this.client.post('/diary/reset/measurements')
    return data
  }

  async resetIndicators(): Promise<{ deleted: number }> {
    const { data } = await this.client.post('/diary/reset/indicators')
    return data
  }

  async resetProgress(): Promise<{ message: string }> {
    const { data } = await this.client.post('/onboarding/reset-all')
    return data
  }

  async shiftTime(days: 1 | 7): Promise<{ shifted: number }> {
    const { data } = await this.client.post('/diary/shift-time', { days })
    return data
  }

  async grantXp(amount: number): Promise<{
    newXp: number
    newLevel: number
    previousLevel: number
    leveledUp: boolean
    tucoinBonus: number
    delta: number
  }> {
    const { data } = await this.client.post('/diary/grant-xp', { amount })
    return data
  }

  async grantLife(delta: number): Promise<{ lives: number; delta: number; noop?: boolean }> {
    const { data } = await this.client.post('/diary/grant-life', { delta })
    return data
  }

  async toggleFreeze(action: 'activate' | 'clear'): Promise<{ ok: boolean; action: string }> {
    const { data } = await this.client.post('/diary/toggle-freeze', { action })
    return data
  }

  async setMode(mode: 'DEMO' | 'TEST_WEEK' | 'ACTIVE', clearCurator = false): Promise<{ ok: boolean; mode: string; clearCurator: boolean }> {
    const { data } = await this.client.post('/diary/set-mode', { mode, clearCurator })
    return data
  }

  // Profile
  async updateProfile(data: {
    firstName?: string
    lastName?: string
    isAnonymous?: boolean
  }) {
    const response = await this.client.put('/users/profile', data)
    return response.data
  }

  // FatSecret
  async getFatSecretStatus() {
    const { data } = await this.client.get('/fatsecret/status')
    return data
  }

  async connectFatSecret(): Promise<{ authUrl: string }> {
    markFatSecretConnectStarted()
    const { data } = await this.client.get('/fatsecret/connect')
    return data
  }

  async disconnectFatSecret() {
    const { data } = await this.client.post('/fatsecret/disconnect')
    return data
  }

  /**
   * #402: `date` (YYYY-MM-DD) — пересинкать конкретный день. Нужен, когда клиент
   * правит дневник FatSecret задним числом: фон смотрит только сегодня, ночная
   * сверка — ±2 дня, и день постарше иначе не обновится никогда. Без даты —
   * прежнее поведение (окно вокруг сегодня).
   */
  async syncFatSecret(date?: string) {
    const { data } = await this.client.post('/fatsecret/sync', date ? { date } : {})
    return data
  }

  // Curator
  async getCuratorClients(): Promise<{ clients: Array<{
    id: string; firstName: string; lastName: string; telegramUsername: string | null;
    telegramAvatar: string | null; level: number; streakDays: number; lives: number;
    isActive: boolean; isOnFreeze: boolean; product: string | null;
    // Паузы программы. isOnFreeze — это личная суточная заморозка стрика,
    // с ними не путать: при паузе отчётов от клиента не ждут вовсе.
    isSuspended?: boolean;
    suspendedReason?: string | null;
    isOnProgramPause?: boolean;
    programPauseUntil?: string | null;
    pendingTrackingCount: number;
    unreadCommentsCount?: number;
    onboardingCompleted?: boolean;
    hasNutritionGoals?: boolean;
    hasProgram?: boolean;
  }>; total: number }> {
    const { data } = await this.client.get('/curator/clients')
    return data
  }

  // #286: инбокс комментариев клиента для куратора — зеркало клиентского
  // GET /diary/all-comments (те же треды/теги, unread = сообщения клиента).
  async getCuratorClientAllComments(clientId: string): Promise<{ comments: import('../components/modals/CommentsInboxSheet').InboxComment[] }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}/all-comments`)
      return data
    })
  }

  async markCuratorClientCommentsRead(clientId: string): Promise<{ updated: number }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.post(`/curator/clients/${clientId}/all-comments/mark-read`, {})
      return data
    })
  }

  /** #334: «Прочитать всё» — массово гасит бейджи «не проверено» по дням клиента. */
  async resolveCuratorClientTracking(clientId: string): Promise<{ resolved: number }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.post(`/curator/clients/${clientId}/tracking/resolve-all`, {})
      return data
    })
  }

  async impersonateClient(clientId: string): Promise<AuthResponse> {
    const { data } = await this.client.post<AuthResponse>(`/curator/impersonate/${clientId}`)
    return data
  }

  /**
   * Switch a dual-identity user between their own curator/client accounts.
   * Mints the counterpart-role token server-side (by shared telegramId) — no
   * Telegram initData needed, so it works in the browser too.
   */
  async switchMode(to: 'client' | 'curator'): Promise<{ token: string; isCurator: boolean; isAlsoClient?: boolean; isAlsoCurator?: boolean; user?: User }> {
    const { data } = await this.client.post<{ token: string; isCurator: boolean; isAlsoClient?: boolean; isAlsoCurator?: boolean; user?: User }>('/auth/switch-mode', { to })
    this.setToken(data.token)
    return data
  }

  async getCuratorStats(): Promise<{
    totalClients: number
    activeClients: number
    inactiveClients: number
    lowLifeClients: number
    averageStreak: number
    programsCount: number
    commentsCount: number
  }> {
    const { data } = await this.client.get('/curator/stats')
    return data
  }

  /** Архив: бывшие клиенты куратора (работа завершена, куратора сейчас нет). */
  async getCuratorArchive(): Promise<{ clients: Array<{
    id: string; firstName: string; lastName: string; telegramUsername: string | null;
    telegramAvatar: string | null; avatarUrl: string | null; product: string | null;
    workStartedAt: string; workEndedAt: string;
  }> }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get('/curator/clients/archive')
      return data
    })
  }

  async getCuratorClient(clientId: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}`)
      return data
    })
  }

  async getCuratorClientTdee(clientId: string, targetRate?: number) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}/tdee`, {
        params: targetRate != null ? { targetRate } : undefined,
      })
      return data as {
        clientId: string
        clientFirstName: string
        targetRate: number
        // #562: цель и сохранённый темп клиента (планка из вкладки «Питание»).
        goal: string | null
        savedTargetRate: number | null
        latest: {
          date: string
          weight: number | null
          ema: number | null
          weeklyDeltaPct: number | null
          avg7Cal: number | null
          tdeeSmooth: number | null
          planKcal: number | null
          misses14: number | null
        }
        traffic: { weekly: 'green'|'yellow'|'red'|'none'; misses: 'green'|'yellow'|'red'|'none' }
        series: Array<{ date: string; weight: number | null; ema: number | null; calories: number | null }>
      }
    })
  }

  async getCuratorClientWorkoutDiary(clientId: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}/workout-diary`)
      return data
    })
  }

  // Те же интерсепторы и ретраи, что у общего клиента, но запрос помечен, и
  // интерсептор ставит ему кураторский токен.
  private readonly curatorClient: CuratorClient = {
    get: (url, config) => this.client.get(url, { ...config, _asCurator: true } as AxiosRequestConfig),
    delete: (url, config) => this.client.delete(url, { ...config, _asCurator: true } as AxiosRequestConfig),
    post: (url, data, config) => this.client.post(url, data, { ...config, _asCurator: true } as AxiosRequestConfig),
    put: (url, data, config) => this.client.put(url, data, { ...config, _asCurator: true } as AxiosRequestConfig),
    patch: (url, data, config) => this.client.patch(url, data, { ...config, _asCurator: true } as AxiosRequestConfig),
  }

  /**
   * Кураторские вызовы работают под кураторским токеном и тогда, когда сессия —
   * просмотр клиента (`enterClient` отложил кураторский токен в
   * `tfa_curator_token`). Токен цепляется к самому запросу, а общий
   * `this.token` не трогаем. Раньше его подменяли на всё время запроса, и
   * параллельный запрос просмотра уходил с кураторским (27.10.2026 Дневник так
   * получил пустой день), а два вызова внахлёст оставляли сессию на
   * кураторском токене (#14 аудита).
   */
  private withCuratorToken<T>(fn: (client: CuratorClient) => Promise<T>): Promise<T> {
    return fn(this.curatorClient)
  }

  async getCuratorTemplates() {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get('/curator/templates')
      return data
    })
  }

  /** #623: сохранить программу из конструктора в личные шаблоны куратора. */
  async saveCuratorTemplate(body: Omit<Parameters<ApiService['assignCuratorProgram']>[1], 'templateId'>) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.post('/curator/templates', body)
      return data
    })
  }

  async deleteCuratorTemplate(templateId: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.delete(`/curator/templates/${templateId}`)
      return data
    })
  }

  async getCuratorClientAnalytics(clientId: string): Promise<{
    analytics: {
      workouts30d: number
      daysInProgram: number
      streakDays: number
      bestStreakDays: number
      avgCalories30d: number | null
      daysWithNutrition30d: number
      strengthGainPct: number | null
      exercises: Array<{
        exerciseId: string
        name: string
        startMax: number
        currentMax: number
        delta: number
        deltaPct: number
        sessions: number
      }>
    }
  }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}/analytics`)
      return data
    })
  }

  async assignCuratorProgram(clientId: string, body: {
    name: string
    templateId?: string
    daysCount: number
    days: Array<{
      dayNumber: number
      exercises: Array<{
        exerciseId?: string
        customName?: string
        orderNum: number
        sets?: number
        reps?: string
        weight?: number
        duration?: number
        intensity?: string
        notes?: string
      }>
    }>
  }) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.post(`/curator/clients/${clientId}/program`, body)
      return data
    })
  }

  async getCuratorClientProgram(clientId: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}/program`)
      return data
    })
  }

  async getCuratorClientNutritionGoals(clientId: string): Promise<{
    goals: Array<{
      id: string
      dayType: 'REGULAR' | 'TRAINING'
      calories: number
      protein: number
      fat: number
      carbs: number
      fiber: number | null
      curatorId: string | null
      curator: { id: string; name: string } | null
      createdAt: string
      updatedAt: string
    }>
  }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}/nutrition-goals`)
      return data
    })
  }

  async saveCuratorClientNutritionGoals(
    clientId: string,
    goals: Array<{
      dayType: 'REGULAR' | 'TRAINING'
      calories: number
      protein: number
      fat: number
      carbs: number
      fiber?: number
    }>,
  ) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.put(`/curator/clients/${clientId}/nutrition-goals`, { goals })
      return data
    })
  }

  /** Включённые метрики питания клиента (управление куратором). */
  async getCuratorClientNutritionMetrics(clientId: string): Promise<{ enabled: string[] }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}/nutrition-metrics`)
      return data
    })
  }

  async saveCuratorClientNutritionMetrics(clientId: string, enabled: string[]): Promise<{ enabled: string[] }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.put(`/curator/clients/${clientId}/nutrition-metrics`, { enabled })
      return data
    })
  }

  /** #400: индивидуальная цель по шагам — прочитать. */
  async getCuratorClientStepsGoal(clientId: string): Promise<{ stepsGoal: number | null; defaultGoal: number }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}/steps-goal`)
      return data
    })
  }

  /** #400: назначить цель или убрать её (null). */
  // #562: цель по весу и темп («планка») — ставит куратор в плане питания.
  async getCuratorClientWeightGoal(clientId: string): Promise<{ goal: string | null; targetRate: number | null; effectiveRate: number; defaultRate: number | null }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get(`/curator/clients/${clientId}/weight-goal`)
      return data
    })
  }

  async saveCuratorClientWeightGoal(clientId: string, patch: { goal?: string | null; targetRate?: number | null }): Promise<{ goal: string | null; targetRate: number | null; effectiveRate: number; defaultRate: number | null }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.put(`/curator/clients/${clientId}/weight-goal`, patch)
      return data
    })
  }

  async saveCuratorClientStepsGoal(clientId: string, stepsGoal: number | null): Promise<{ stepsGoal: number | null; defaultGoal: number }> {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.put(`/curator/clients/${clientId}/steps-goal`, { stepsGoal })
      return data
    })
  }

  async updateProgramDays(clientId: string, programId: string, daysCount: number) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.patch(`/curator/clients/${clientId}/program/${programId}/days`, { daysCount })
      return data
    })
  }

  async activateCuratorProgram(clientId: string, programId: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.post(`/curator/clients/${clientId}/program/${programId}/activate`)
      return data
    })
  }

  async forkCuratorProgram(clientId: string, programId: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.post(`/curator/clients/${clientId}/program/${programId}/fork`)
      return data
    })
  }

  async discardCuratorDraft(clientId: string, programId: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.delete(`/curator/clients/${clientId}/program/${programId}`)
      return data
    })
  }

  /**
   * Точечно правит замены (от куратора) и YouTube-видео техники для упражнения
   * клиента прямо на активной программе (без форка).
   */
  async saveCuratorProgramExerciseExtras(
    exerciseId: string,
    body: {
      replacementIds?: string[]
      customReplacements?: Array<{ exerciseId?: string | null; name: string; sets?: number | null; reps?: string | null; weight?: number | null }>
      videoUrl?: string | null
    },
  ) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.patch(`/curator/program-exercises/${exerciseId}/extras`, body)
      return data
    })
  }

  async getCuratorExercises(search?: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.get('/curator/exercises', {
        params: search ? { search } : undefined,
      })
      return data
    })
  }

  async getCuratorClientExerciseHistory(clientId: string, key: string | { exerciseId?: string | null; name?: string | null }) {
    return this.withCuratorToken(async (client) => {
      const params: Record<string, string> = {}
      if (typeof key === 'string') params.exerciseId = key
      else if (key.exerciseId) params.exerciseId = key.exerciseId
      else if (key.name) params.name = key.name
      const { data } = await client.get(`/curator/clients/${clientId}/exercise-history`, { params })
      return data
    })
  }

  async addProgramExercise(
    clientId: string,
    programId: string,
    dayNumber: number,
    body: { exerciseId: string; orderNum?: number; sets?: number | null; reps?: string | null; weight?: number | null; duration?: number | null; intensity?: string | null }
  ) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.post(
        `/curator/clients/${clientId}/program/${programId}/days/${dayNumber}/exercises`,
        body
      )
      return data
    })
  }

  async updateProgramExercise(exerciseId: string, body: { sets?: number | null; reps?: string | null; weight?: number | null; duration?: number | null; intensity?: string | null }) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.put(`/curator/program-exercises/${exerciseId}`, body)
      return data
    })
  }

  async deleteProgramExercise(exerciseId: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.delete(`/curator/program-exercises/${exerciseId}`)
      return data
    })
  }

  // Curator comments on client reports (polymorphic). Always curator-token.
  async createCuratorComment(body: { targetType: string; targetId: string; text: string }) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.post('/curator/comments', body)
      return data
    })
  }

  async updateCuratorComment(commentId: string, text: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.put(`/curator/comments/${commentId}`, { text })
      return data
    })
  }

  async deleteCuratorComment(commentId: string) {
    return this.withCuratorToken(async (client) => {
      const { data } = await client.delete(`/curator/comments/${commentId}`)
      return data
    })
  }

  // Settings (doc 16)
  async getSettings() {
    const { data } = await this.client.get('/users/settings')
    return data
  }

  async updateSettings(settings: {
    streakReminder?: boolean
    diaryReminder?: boolean
    weeklyResults?: boolean
    newLectures?: boolean
    challenges?: boolean
    friendAchievements?: boolean
    dailySummary?: boolean
    // #345: дубль комментариев куратора в TG
    curatorComments?: boolean
    isAnonymous?: boolean
    showProfile?: boolean
    // #331/#332: персистентные UI-флаги (частичный патч, сервер мержит)
    uiFlags?: { fsHintHidden?: boolean; dismissedSurveys?: string[] }
  }) {
    const { data } = await this.client.put('/users/settings', settings)
    return data
  }

  // Achievements
  async getAchievements() {
    const { data } = await this.client.get('/achievements')
    if (data.unlocked || data.locked) {
      const flatten = (a: Record<string, unknown>, isUnlocked: boolean) => ({
        ...a,
        isUnlocked,
        target: a.progressMax,
      })
      return [
        ...(data.unlocked || []).map((a: Record<string, unknown>) => flatten(a, true)),
        ...(data.locked || []).map((a: Record<string, unknown>) => flatten(a, false)),
      ]
    }
    return data
  }
}

export const api = new ApiService()

/**
 * Разбирает axios-ошибку в человекочитаемые детали (bug #213): HTTP-статус, код
 * бэкенда (тело ошибки = { error: <code>, message }), эндпоинт и метод. Нужно и
 * для экрана ошибки, и для диагностического beacon — вместо голого
 * «Request failed with status code 403».
 */
export function describeApiError(err: unknown): {
  status?: number
  code?: string
  endpoint?: string
  method?: string
  message: string
  isNetwork: boolean
} {
  const ax = err as AxiosError<{ error?: string; message?: string }> | undefined
  const status = ax?.response?.status
  const data = ax?.response?.data
  // Бэкенд кладёт код в data.error, человеческий текст — в data.message.
  const code = (data && typeof data === 'object' ? data.error : undefined) || undefined
  const serverMessage = data && typeof data === 'object' ? data.message : undefined
  const endpoint = ax?.config?.url
  const method = ax?.config?.method?.toUpperCase()
  // Ответа не было вовсе: обрыв соединения или таймаут. Отличать важно — сброс
  // сессии тут не лечит, а лечит повтор, когда связь вернётся.
  const isNetwork = !!ax?.isAxiosError && !ax.response
  const message = serverMessage || (err as Error)?.message || 'Неизвестная ошибка'
  return { status, code, endpoint, method, message, isNetwork }
}
